import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

export function useToast() {
  const success = useCallback((message: string, duration = 5000) => {
    return toast.success(message, { duration });
  }, []);

  const error = useCallback((message: string, duration = 5000) => {
    return toast.error(message, { duration });
  }, []);

  const warning = useCallback((message: string, duration = 5000) => {
    return toast.custom(
      (t) => (
        <div
          className={`flex items-center gap-3 bg-orange-500 text-white px-4 py-3 rounded-lg shadow-lg ${
            t.visible ? 'animate-enter' : 'animate-leave'
          }`}
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{message}</span>
        </div>
      ),
      { duration }
    );
  }, []);

  const info = useCallback((message: string, duration = 5000) => {
    return toast.custom(
      (t) => (
        <div
          className={`flex items-center gap-3 bg-[#0f6cbd] text-white px-4 py-3 rounded-lg shadow-lg ${
            t.visible ? 'animate-enter' : 'animate-leave'
          }`}
        >
          <Info className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{message}</span>
        </div>
      ),
      { duration }
    );
  }, []);

  const addToast = useCallback((type: ToastType, message: string, duration = 5000) => {
    switch (type) {
      case 'success':
        return success(message, duration);
      case 'error':
        return error(message, duration);
      case 'warning':
        return warning(message, duration);
      case 'info':
        return info(message, duration);
    }
  }, [success, error, warning, info]);

  const removeToast = useCallback((id: string) => {
    toast.dismiss(id);
  }, []);

  return {
    toasts: [], // Mantenuto per retrocompatibilità, non più usato
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };
}
